import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Select from 'react-select';
import { Form, InputGroup, ButtonGroup } from 'react-bootstrap';
import { setHours, setMinutes, subDays, setSeconds } from 'date-fns';
import { DATA_RANGE } from '@awp/constants';
import { NumericFormat } from 'react-number-format';
import PropTypes from 'prop-types';

import { setDates, setDataRange, selectDataRange } from '@stores/dataVisualizationSlice';
import {
  setEndDateEpoch,
  setDataRange as setDataRangeWs,
  setAllFilteredWeatherStations,
} from '@awp/stores/weatherStationSlice';

const dataRangeOptions = [
  { label: '4 Days', value: 4 },
  { label: '7 Days', value: 7 },
  { label: '14 Days', value: 14 },
  { label: '28 Days', value: 28 },
  { label: 'Custom', value: DATA_RANGE.CUSTOM }, //the select doesn't set the item when it's -1
];

const getDataRangeOption = (value) => {
  let option = dataRangeOptions.filter((o) => o.value === value);
  if (option.length === 0) {
    option = dataRangeOptions.filter((o) => o.value === DATA_RANGE.CUSTOM);
  }

  return option;
};

const AwpDataRange = (Props) => {
  const dispatch = useDispatch();

  const dataRange = useSelector(selectDataRange);

  const [isCustomDataRange, setIsCustomDataRange] = useState(false);
  const [dataRangeValue, setDataRangeValue] = useState(dataRange);
  const [selectedValue, setSelectedValue] = useState(getDataRangeOption(dataRangeValue));

  // when dataVisualization state is changed
  useEffect(() => {
    setDataRangeValue(dataRange);
    const dataRangeOption = getDataRangeOption(dataRange);
    setSelectedValue(dataRangeOption);
    setIsCustomDataRange(dataRangeOption[0].value === DATA_RANGE.CUSTOM);
  }, [dataRange, Props.endDateValue, Props.weatherStation]);

  useEffect(() => {
    dispatch(setDataRangeWs(dataRange ?? 0));
    dispatch(setAllFilteredWeatherStations());
  }, [dataRange]);

  const dispatchDataRangeChanges = (newDataRange, endDateValue) => {
    let todayDate = new Date();

    setDataRangeValue(newDataRange);
    if (!isNaN(newDataRange)) {
      dispatch(setDataRange({ value: newDataRange }));

      dispatch(
        setDates({
          endDate: {
            value: setSeconds(setMinutes(setHours(endDateValue, 23), 59), 0).getTime(),
            error: '',
            disabled: false,
          },
          startDate: {
            value:
              new Date(endDateValue).toDateString() == todayDate.toDateString()
                ? setSeconds(setMinutes(setHours(subDays(endDateValue, newDataRange), 0), 0), 0).getTime()
                : setSeconds(setMinutes(setHours(subDays(endDateValue, newDataRange - 1), 0), 0), 0).getTime(),
            error: '',
            disabled: false,
          },
        }),
      );
    }
  };

  const dataRangeChange = (value) => {
    setSelectedValue(getDataRangeOption(value));

    if (value === DATA_RANGE.CUSTOM) {
      setIsCustomDataRange(true);
    } else {
      setIsCustomDataRange(false);
      setDataRangeValue(value);
      dispatchDataRangeChanges(value, Props.endDateValue);
    }
  };

  return (
    <>
      <Form.Label>
        <b>Display Range</b>
      </Form.Label>
      <InputGroup className="mb-3" style={{ zIndex: '1000' }}>
        <Select
          className="data-range-select"
          options={dataRangeOptions}
          value={selectedValue}
          onChange={(e) => {
            dataRangeChange(e.value);
          }}
        />
        <ButtonGroup className="data-range-input">
          <NumericFormat
            isAllowed={(values) => {
              const { floatValue } = values;

              return floatValue === undefined || (floatValue <= DATA_RANGE.MAXIMUM && floatValue >= DATA_RANGE.MINIMUM);
            }}
            onKeyUp={(e) => {
              e.persist();
              setTimeout(() => {
                if (e.target != null) dispatchDataRangeChanges(parseInt(e.target.value), Props.endDateValue);
              }, 1500);
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            allowNegative={false}
            decimalScale={0}
            disabled={!isCustomDataRange}
            valueIsNumericString={true}
            className="form-control input-sm data-range-custom"
            thousandSeparator={true}
            value={dataRangeValue}
          />
        </ButtonGroup>
      </InputGroup>
    </>
  );
};

AwpDataRange.propTypes = {
  endDateValue: PropTypes.number.isRequired,
};

export default AwpDataRange;
